import {
  Divider,
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  Alert,
} from "@mui/material";
import TicketSearch from "../../../../Components/TicketSearch";
import React, { useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useUpdate from "../../../../api/useUpdate";
import SkeletonLoading from "./loading/SkeletonLoading";
import { useTranslation } from "react-i18next";

const getButtonColor = (status) => {
  switch (status) {
    case 1:
      return "#FFC107";
    case 2:
      return "#4CAF50";
    case 3:
      return "#F44336";
    default:
      return "#a7e2f9";
  }
};

const TestsToBeDone = () => {
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const auth = useAuth();
  const token = auth?.user?.token;

  // Status filtering logic
  const status = value === 0 ? 1 : value === 1 ? 2 : 3;

  // Fetch ultrasounds only for the selected ticket (ticket-first flow)
  const { data, isLoading, isError, error } = useFetchObjects(
    selectedTicket?.id != null ? ["ultrasounds", selectedTicket.id, status] : null,
    selectedTicket?.id != null
      ? `ultrasounds/?ticket=${selectedTicket.id}&status=${status}`
      : "",
    token,
  );

  // Update ultrasound status
  const { handleUpdate } = useUpdate("ultrasounds", token);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedUltrasound, setSelectedUltrasound] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event, ultrasound) => {
    setAnchorEl(event.currentTarget);
    setSelectedUltrasound(ultrasound);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedUltrasound(null);
  };

  const handleStatusChange = (newStatus) => {
    const formData = new FormData();
    formData.append("status", newStatus);
    handleUpdate(selectedUltrasound.id, formData);
    handleClose();
  };
  const UltrasoundStatus = {
    1: t("ultrasound.pending"),
    2: t("ultrasound.completed"),
    3: t("ultrasound.canceled"),
  };

  const buttonStyle = {
    border: "2px solid",
    borderRadius: "0.5em",
    color: "#a7e2f9",
    margin: "1em",
  };
  const tableStyle = {
    textAlign: "center",
  };

  return (
    <Grid item xs={12}>
      {selectedTicket && isLoading ? (
        <SkeletonLoading />
      ) : (
        <Paper sx={{ boxShadow: "0px 0px 0px" }}>
          <Grid container padding={"10px"} spacing={2}>
            <Grid item xs={12} md={8}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="scrollable force tabs example"
              >
                <Tab
                  sx={{ textTransform: "capitalize" }}
                  label={t("ultrasound.ultrasoundToBeDone")}
                />
                <Tab
                  sx={{ textTransform: "capitalize" }}
                  label={t("ultrasound.done")}
                />
                <Tab
                  sx={{ textTransform: "capitalize" }}
                  label={t("ultrasound.cancelled")}
                />
              </Tabs>
            </Grid>
            <Grid item xs={12} md={4}>
              <TicketSearch
                value={selectedTicket}
                onChange={setSelectedTicket}
                placeholder={t("ultrasound.searchByTicket") || "Search by Ticket Number"}
                size="small"
              />
            </Grid>
            <Divider sx={{ mt: 2, width: "100%" }} />

            {!selectedTicket && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {t("ultrasound.selectTicketToSeeTests") ||
                    "Search and select a ticket to see ultrasound tests for that visit."}
                </Alert>
              </Grid>
            )}

            {isError && (
              <Typography>
                {t("ultrasound.error")}: {error.message}
              </Typography>
            )}
            {selectedTicket && !isLoading && (
              <TableContainer sx={{ mt: 2 }}>
                <Table sx={{ minWidth: "100%" }} aria-label="simple table">
                  <TableHead style={{ backgroundColor: "#f2f2f2" }}>
                    <TableRow>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.time")}
                      </TableCell>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.date")}
                      </TableCell>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.patientName")}
                      </TableCell>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.doctor")}
                      </TableCell>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.testType")}
                      </TableCell>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.amount")}
                      </TableCell>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.paymentStatus")}
                      </TableCell>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.status")}
                      </TableCell>
                      <TableCell style={tableStyle}>
                        {t("ultrasound.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {(data ?? []).map((item, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell
                          style={tableStyle}
                          component="th"
                          scope="row"
                        >
                          {item.date}
                        </TableCell>
                        <TableCell style={tableStyle}>{item.date}</TableCell>
                        <TableCell style={tableStyle}>
                          {item.patient_display}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {item.doctor_display}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {item.ultrasound_type_display}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {item.total_amount} ؋
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {item.is_paid ? (
                            <Typography color="green">
                              {" "}
                              {t("ultrasound.paid")}
                            </Typography>
                          ) : (
                            <Typography color="red">
                              {" "}
                              {t("ultrasound.unpaid")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              color: getButtonColor(item.status),
                            }}
                          >
                            {UltrasoundStatus[item.status]}
                          </Typography>
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {item.status === 1 ? (
                            <Button
                              id="demo-positioned-button"
                              aria-controls={
                                open ? "demo-positioned-menu" : undefined
                              }
                              aria-haspopup="true"
                              aria-expanded={open ? "true" : undefined}
                              onClick={(event) => handleClick(event, item)}
                            >
                              <MoreHorizIcon />
                            </Button>
                          ) : (
                            <Typography>
                              {t("ultrasound.noFurtherAction")}
                            </Typography>
                          )}
                          <Menu
                            id="demo-positioned-menu"
                            aria-labelledby="demo-positioned-button"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                            PaperProps={{
                              sx: {
                                boxShadow: "0px 20px 10px rgba(0, 0, 0, 0.02)",
                                "&:before": { boxShadow: "none" },
                                border: "1px solid lightgray",
                                borderRadius: "1em",
                                padding: "10px",
                              },
                            }}
                          >
                            <Grid>
                              <Button
                                variant="none"
                                onClick={handleClose}
                                style={{
                                  position: "absolute",
                                  top: "0.01em",
                                  right: "0.01em",
                                }}
                              >
                                <CancelTwoToneIcon
                                  style={{ color: "#a7e2f9" }}
                                />
                              </Button>
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  color: "#081c54",
                                  marginTop: "1em",
                                  marginLeft: "0.5em",
                                  fontSize: "27px",
                                }}
                              >
                                {t("ultrasound.task")}
                              </Typography>
                              <Typography style={{ margin: "1em" }}>
                                {t("ultrasound.ultrasoundTest")}:{" "}
                                {item.ultrasound_type_display}
                              </Typography>
                              <Typography style={{ margin: "1em" }}>
                                {t("ultrasound.status")}:{" "}
                                {UltrasoundStatus[item.status]}
                              </Typography>
                              {item.status === 1 && (
                                <Box
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <MenuItem
                                    style={{
                                      ...buttonStyle,
                                      border: `2px solid ${getButtonColor(
                                        item.status,
                                      )}`,
                                      color: getButtonColor(item.status),
                                    }}
                                    onClick={() => handleStatusChange(2)} // Mark as Completed
                                  >
                                    {t("ultrasound.markCompleted")}
                                  </MenuItem>
                                  <MenuItem
                                    style={{
                                      ...buttonStyle,
                                      border: `2px solid ${getButtonColor(
                                        item.status,
                                      )}`,
                                      color: getButtonColor(item.status),
                                    }}
                                    onClick={() => handleStatusChange(3)} // Mark as Cancelled
                                  >
                                    {t("ultrasound.markCancelled")}
                                  </MenuItem>
                                </Box>
                              )}
                            </Grid>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Paper>
      )}
    </Grid>
  );
};

export default TestsToBeDone;
